import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';

import { useUserDetails } from '../../../store/slices/authSlice';
import { pbxDtmfListGet, usePbxDtmfList } from '../../../store/slices/tenantSlices/settingSlice';

// Static reference of EasyPBX feature codes (IP-phone / softphone, no agent GUI).
// Display only — the in-call transfer codes (22#-77#) are handled by FreeSWITCH
// bind_digit_action and do NOT need rows in easy_pbx_dtmf, so showing them here has
// no effect on the call flow. The '11'=MANUAL_DIAL row (below) drives 11#<number>.
const FEATURE_CODES = [
  { code: '11#<number>', desc: 'Manual dial the number directly' },
  { code: '*22<id>#', desc: 'Blind transfer → Agent (Users ID)' },
  { code: '*33<id>#', desc: 'Blind transfer → Process (Process ID)' },
  { code: '*44<id>#', desc: 'Blind transfer → IVR (IVR ID)' },
  { code: '*55<id>#', desc: 'Attended transfer → Agent (Users ID)' },
  { code: '*66<id>#', desc: 'Attended transfer → Process (Process ID)' },
  { code: '*77<id>#', desc: 'Attended transfer → IVR (IVR ID)' }
];

const PbxDtmf = () => {
  const dispatch = useDispatch();
  const pbxDtmfList = usePbxDtmfList();
  const userDetails = useUserDetails();

  const [isLoading, setIsLoading] = useState(false);
  const handleFormSubmit = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        company_id: userDetails?.companyId
      };
      await dispatch(pbxDtmfListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  useEffect(() => {
    if (userDetails?.companyId) {
      handleFormSubmit();
    }
  }, [userDetails?.companyId]);

  return (
    <div>
      <AdminHeader title="PBX DTMF" isAdd={false} />

      {/* Feature-code reference (static, display only - no DB / no flow impact). */}
      <Card className="p-0 m-0 mb-2">
        <div className="p-3">
          <div className="fw-bold mb-2">Feature Codes (IP-phone / softphone, no GUI)</div>
          <div className="row">
            {FEATURE_CODES.map((f, i) => (
              <div className="col-md-4 col-sm-6 mb-1" key={i}>
                <span className="fw-bold">{f.code}</span> — {f.desc}
              </div>
            ))}
          </div>
          <div className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
            In-call transfer codes are dialled during a live call, in the form{' '}
            <span className="fw-bold">*&lt;code&gt;&lt;id&gt;#</span> (start with *, end with #).
            11#&lt;number&gt; dials a number directly. Requires the agent&apos;s process to have Easy
            PBX enabled.
          </div>
          <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
            Example: during a call dial <span className="fw-bold">*22495#</span> to blind-transfer the
            caller to the agent whose ID is 495. The <span className="fw-bold">ID</span> is shown in the
            Users / Process / IVR pages.
          </div>
          <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
            Attended transfer (55 / 66 / 77): after dialling the code, consult the target, then press{' '}
            <span className="fw-bold">0</span> = conference, <span className="fw-bold">*</span> = complete
            the transfer, <span className="fw-bold">#</span> = cancel (return to the caller).
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 384px)' }}>
          <CallexLoader />
        </Card>
      ) : pbxDtmfList?.data?.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 384px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th style={{ width: '33.33%' }}>Date</th>
                  <th style={{ width: '33.33%' }}>DTMF</th>
                  <th style={{ width: '33.33%' }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {pbxDtmfList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.date_and_time}</td>
                      <td>{val?.dtmf}</td>
                      <td>{val?.type}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 384px)' }}>
          No data available
        </Card>
      )}
    </div>
  );
};

export default PbxDtmf;
